// Interactive Gantt chart.
// Each task gets one main bar (work in progress, priority colour) followed by
// a recovery bar (until break-even, hatched amber) and a net-positive bar
// (after break-even to horizon end, green).
//
// Two drag interactions:
//   • Coloured work bar — horizontal drag to change `startMonth` (half-month snap).
//   • Title area on the left — vertical drag to reorder the row in the list.
// A click without movement on either falls through to opening the task page.

import { useState } from "react";
import { taskEconomics, fmtMonth } from "../calc.js";

const MONTH_LABELS = ["May","Jun","Jul","Aug","Sep","Oct","Nov","Dec",
                      "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_W = 60;
const LABEL_W = 320;
const ROW_H   = 36;
const MS_BAND = 36;
const HEADER_H = 50 + MS_BAND;
const HORIZON = 20;
const SNAP_MONTHS = 0.5;
const DRAG_THRESHOLD_PX = 3;

const PRIO_COLOR = { P1:"var(--p1)", P2:"var(--p2)", P3:"var(--p3)", P4:"var(--p4)" };

const snapStart   = (m) => Math.round(m / SNAP_MONTHS) * SNAP_MONTHS;
const clampStart  = (m) => Math.max(0, Math.min(HORIZON - SNAP_MONTHS, m));

export default function GanttChart({ tasks, milestones = [], settings, onMoveTask, onReorderTasks, onMoveGate }) {
  if (!settings) return null;

  // Horizontal drag (reschedule): { id, startClientX, originStartMonth, dxMonths, moved }
  const [hdrag, setHdrag] = useState(null);
  // Vertical drag (reorder):     { id, fromIndex, startClientY, dyPx, dropIndex, moved }
  const [vdrag, setVdrag] = useState(null);
  // Gate (milestone) horizontal drag: { id, startClientX, originMonth, dxMonths, moved }
  const [gdrag, setGdrag] = useState(null);

  // --- effective values (apply in-flight drags before measuring) ----------
  const effStartOf = (t) => {
    if (hdrag && hdrag.id === t.id) {
      return clampStart(snapStart(hdrag.originStartMonth + hdrag.dxMonths));
    }
    return Number(t.startMonth);
  };

  const rowCount = tasks.length;
  const visualIndex = (origIndex) => {
    if (!vdrag) return origIndex;
    const { fromIndex, dropIndex } = vdrag;
    if (origIndex === fromIndex) return dropIndex;
    if (fromIndex < dropIndex && origIndex > fromIndex && origIndex <= dropIndex) return origIndex - 1;
    if (fromIndex > dropIndex && origIndex < fromIndex && origIndex >= dropIndex) return origIndex + 1;
    return origIndex;
  };

  const rows = tasks.map((t, origIndex) => {
    const effStart = effStartOf(t);
    const tEff = effStart === Number(t.startMonth) ? t : { ...t, startMonth: effStart };
    return {
      task: t,
      origIndex,
      vIndex: visualIndex(origIndex),
      effStart,
      econ: taskEconomics(tEff, settings, HORIZON),
    };
  });
  const econById     = Object.fromEntries(rows.map(r => [r.task.id, r.econ]));
  const effStartById = Object.fromEntries(rows.map(r => [r.task.id, r.effStart]));
  const vIndexById   = Object.fromEntries(rows.map(r => [r.task.id, r.vIndex]));

  const chartHeight = HEADER_H + rowCount * ROW_H + 60;
  const chartWidth  = LABEL_W + HORIZON * MONTH_W;
  const todayX = LABEL_W + (17/30) * MONTH_W;     // 18 May 2026 ≈ day 17

  const clamp = (x) => Math.max(LABEL_W, Math.min(LABEL_W + HORIZON*MONTH_W, x));

  // --- horizontal drag handlers (work bar) -------------------------------
  const moveDraggable = typeof onMoveTask === "function";

  const onBarPointerDown = (e, t) => {
    if (!moveDraggable || e.button !== 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    setHdrag({
      id: t.id,
      startClientX: e.clientX,
      originStartMonth: Number(t.startMonth),
      dxMonths: 0,
      moved: false,
    });
  };
  const onBarPointerMove = (e) => {
    if (!hdrag) return;
    const dxPx = e.clientX - hdrag.startClientX;
    const moved = hdrag.moved || Math.abs(dxPx) > DRAG_THRESHOLD_PX;
    setHdrag({ ...hdrag, dxMonths: dxPx / MONTH_W, moved });
  };
  const onBarPointerUp = (e, t) => {
    if (!hdrag) return;
    const moved = hdrag.moved;
    const finalStart = clampStart(snapStart(hdrag.originStartMonth + hdrag.dxMonths));
    const origin = hdrag.originStartMonth;
    setHdrag(null);
    if (!moved) {
      window.location.hash = `#/task/${t.id}`;
      return;
    }
    if (finalStart !== origin) onMoveTask(t.id, { startMonth: finalStart });
  };
  const onBarPointerCancel = () => setHdrag(null);

  // --- vertical drag handlers (title area / reorder) ---------------------
  const reorderEnabled = typeof onReorderTasks === "function" && rowCount > 1;

  const onLabelPointerDown = (e, t, origIndex) => {
    if (!reorderEnabled || e.button !== 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    setVdrag({
      id: t.id,
      fromIndex: origIndex,
      startClientY: e.clientY,
      dyPx: 0,
      dropIndex: origIndex,
      moved: false,
    });
  };
  const onLabelPointerMove = (e) => {
    if (!vdrag) return;
    const dyPx = e.clientY - vdrag.startClientY;
    const moved = vdrag.moved || Math.abs(dyPx) > DRAG_THRESHOLD_PX;
    let dropIndex = vdrag.fromIndex + Math.round(dyPx / ROW_H);
    if (dropIndex < 0) dropIndex = 0;
    if (dropIndex > rowCount - 1) dropIndex = rowCount - 1;
    setVdrag({ ...vdrag, dyPx, dropIndex, moved });
  };
  const onLabelPointerUp = (e, t) => {
    if (!vdrag) return;
    const { moved, fromIndex, dropIndex } = vdrag;
    setVdrag(null);
    if (!moved) {
      window.location.hash = `#/task/${t.id}`;
      return;
    }
    if (dropIndex === fromIndex) return;
    const reordered = [...tasks];
    const [moving] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moving);
    onReorderTasks(reordered.map(x => x.id));
  };
  const onLabelPointerCancel = () => setVdrag(null);

  // --- gate (milestone diamond) horizontal drag --------------------------
  const gateDraggable = typeof onMoveGate === "function";

  const effGateMonth = (ms) => {
    if (gdrag && gdrag.id === ms.id) {
      return clampStart(snapStart(gdrag.originMonth + gdrag.dxMonths));
    }
    return Number(ms.month);
  };

  const onGatePointerDown = (e, ms) => {
    if (!gateDraggable || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    setGdrag({ id: ms.id, startClientX: e.clientX, originMonth: Number(ms.month), dxMonths: 0, moved: false });
  };
  const onGatePointerMove = (e) => {
    if (!gdrag) return;
    const dxPx = e.clientX - gdrag.startClientX;
    const moved = gdrag.moved || Math.abs(dxPx) > DRAG_THRESHOLD_PX;
    setGdrag({ ...gdrag, dxMonths: dxPx / MONTH_W, moved });
  };
  const onGatePointerUp = (e, ms) => {
    if (!gdrag) return;
    const moved = gdrag.moved;
    const finalMonth = clampStart(snapStart(gdrag.originMonth + gdrag.dxMonths));
    const origin = gdrag.originMonth;
    setGdrag(null);
    if (moved && finalMonth !== origin) onMoveGate(ms.id, { month: finalMonth });
  };
  const onGatePointerCancel = () => setGdrag(null);

  return (
    <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width={chartWidth} height={chartHeight}
         xmlns="http://www.w3.org/2000/svg" fontFamily="-apple-system, BlinkMacSystemFont, Inter, sans-serif">
      <defs>
        <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#fca5a5"/>
        </marker>
        <pattern id="hatch-amber" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <rect width="3" height="6" fill="var(--recovery)"/>
        </pattern>
      </defs>

      {/* Year band */}
      <rect x={LABEL_W} y="0" width={8*MONTH_W} height="22" fill="#1f2937"/>
      <text x={LABEL_W + 4*MONTH_W} y="15" textAnchor="middle" fill="#e5e7eb" fontWeight="600" fontSize="11">2026</text>
      <rect x={LABEL_W + 8*MONTH_W} y="0" width={12*MONTH_W} height="22" fill="#0b1220"/>
      <text x={LABEL_W + 14*MONTH_W} y="15" textAnchor="middle" fill="#e5e7eb" fontWeight="600" fontSize="11">2027</text>

      {/* Month labels */}
      <g fontSize="11" fill="#94a3b8" textAnchor="middle">
        {MONTH_LABELS.map((m,i) => <text key={i} x={LABEL_W + (i+0.5)*MONTH_W} y={40}>{m}</text>)}
      </g>

      {/* Quarter shading */}
      <g opacity="0.6">
        {[...Array(HORIZON).keys()].map(i => i%6<3 ? (
          <rect key={i} x={LABEL_W + i*MONTH_W} y={50} width={MONTH_W} height={chartHeight-80}
                fill={i%2===0 ? "#0b1220" : "#0e1626"}/>
        ) : null)}
      </g>

      {/* Gridlines */}
      <g stroke="#1f2937" strokeWidth="1">
        {[...Array(HORIZON+1).keys()].map(i => (
          <line key={i} x1={LABEL_W + i*MONTH_W} y1={50} x2={LABEL_W + i*MONTH_W} y2={chartHeight-30}/>
        ))}
      </g>

      {/* Today marker */}
      <line x1={todayX} y1={50} x2={todayX} y2={chartHeight-30}
            stroke="#ef4444" strokeWidth="1.5" strokeDasharray="3,3"/>
      <text x={todayX+4} y={15} fontSize="10" fill="#ef4444">Today · 18 May 2026</text>

      {/* External milestones (draggable diamonds) */}
      <g>
        {milestones.map((ms, i) => {
          const mEff = effGateMonth(ms);
          const x = LABEL_W + mEff*MONTH_W;
          const y = 56 + (i%3)*10;
          const isDragging = gdrag && gdrag.id === ms.id && gdrag.moved;
          return (
            <g key={ms.id || i}>
              {/* Hit area for pointer drag — larger than the visible diamond */}
              <rect x={x-9} y={y-9} width="18" height="18" fill="transparent"
                    style={{ cursor: gateDraggable ? (isDragging ? "grabbing" : "grab") : "default", touchAction: "none" }}
                    onPointerDown={(e) => onGatePointerDown(e, ms)}
                    onPointerMove={onGatePointerMove}
                    onPointerUp={(e) => onGatePointerUp(e, ms)}
                    onPointerCancel={onGatePointerCancel}>
                <title>{ms.label} — {fmtMonth(mEff)}{gateDraggable ? " · drag to reschedule" : ""}</title>
              </rect>
              <polygon points={`${x-5},${y} ${x},${y-5} ${x+5},${y} ${x},${y+5}`}
                       fill="var(--milestone)" pointerEvents="none"/>
              <text x={x+9} y={y+3} fontSize="10" fill="#fca5a5" pointerEvents="none">{ms.label}</text>
              {isDragging && (
                <text x={x+9} y={y+15} fontSize="10" fill="#fbbf24" pointerEvents="none">
                  → {fmtMonth(mEff)} (m {mEff.toFixed(1)})
                </text>
              )}
            </g>
          );
        })}
      </g>

      {/* Task rows */}
      {rows.map((r) => {
        const t = r.task;
        const e = r.econ;
        const effStart = r.effStart;
        const isHDragging = hdrag && hdrag.id === t.id && hdrag.moved;
        const isVDragging = vdrag && vdrag.id === t.id && vdrag.moved;

        // Row y: dragged row floats with the pointer; others snap to vIndex.
        const rowY = isVDragging
          ? HEADER_H + r.origIndex * ROW_H + vdrag.dyPx
          : HEADER_H + r.vIndex   * ROW_H;
        const cy = rowY + ROW_H/2;

        const start = LABEL_W + effStart * MONTH_W;
        const endWork = LABEL_W + e.completionMonth * MONTH_W;
        const endBE   = LABEL_W + Math.min(HORIZON, Math.max(0, e.breakevenMonth)) * MONTH_W;
        const endHorizon = LABEL_W + HORIZON*MONTH_W;
        const colorWork = PRIO_COLOR[t.priority] || "var(--p3)";

        const beVisible = Number.isFinite(e.breakevenMonth) && e.breakevenMonth <= HORIZON && e.breakevenMonth > e.completionMonth;
        const endVisible = e.completionMonth < HORIZON;

        const inactive = t.active === false;
        const rowOpacity = (isVDragging ? 0.85 : 1) * (inactive ? 0.4 : 1);
        const rowShadow  = isVDragging ? "drop-shadow(0 4px 8px rgba(0,0,0,0.6))" : "none";
        const rowFilter  = inactive
          ? `${rowShadow === "none" ? "" : rowShadow + " "}grayscale(60%)`
          : rowShadow;

        return (
          <g key={t.id} style={{ filter: rowFilter, opacity: rowOpacity, transition: isVDragging ? "none" : "transform 120ms ease, y 120ms ease" }}>
            <rect x="0" y={rowY} width={chartWidth} height={ROW_H} fill={r.origIndex%2===0 ? "#0a1120" : "#0d1426"}/>

            {/* Reorder drag handle — invisible rect over the label column.
                Hosts the title/subtitle text inside so they move with the row. */}
            <rect
              x="0" y={rowY} width={LABEL_W} height={ROW_H} fill="transparent"
              style={{
                cursor: reorderEnabled ? (isVDragging ? "grabbing" : "ns-resize") : "pointer",
                touchAction: "none",
              }}
              onPointerDown={(e) => onLabelPointerDown(e, t, r.origIndex)}
              onPointerMove={onLabelPointerMove}
              onPointerUp={(e) => onLabelPointerUp(e, t)}
              onPointerCancel={onLabelPointerCancel}
            >
              <title>{reorderEnabled ? "Drag up/down to reorder · click to open" : "Click to open"}</title>
            </rect>
            <text x="10" y={cy-3} fontSize="12" fontWeight="600" fill="#e5e7eb" pointerEvents="none"
                  textDecoration={inactive ? "line-through" : undefined}>
              #{t.briefNo} {t.title.slice(0,42)}{t.title.length>42?"…":""}
            </text>
            <text x="10" y={cy+12} fontSize="10" fill="#94a3b8" pointerEvents="none">
              {t.priority} · {t.status} · {Number(t.effortPw).toFixed(1)} pw · {Number(t.fteAllocation).toFixed(2)} FTE
              {inactive ? " · DISABLED" : ""}
            </text>

            {/* Work bar (priority colour) — draggable horizontally */}
            <rect
              x={clamp(start)} y={rowY+8}
              width={Math.max(2, clamp(endWork)-clamp(start))}
              height={ROW_H-16} rx="3" fill={colorWork}
              style={{
                cursor: moveDraggable ? (isHDragging ? "grabbing" : "grab") : "pointer",
                touchAction: "none",
              }}
              onPointerDown={(e) => onBarPointerDown(e, t)}
              onPointerMove={onBarPointerMove}
              onPointerUp={(e) => onBarPointerUp(e, t)}
              onPointerCancel={onBarPointerCancel}
            >
              <title>
                {moveDraggable ? "Drag to reschedule · click to open" : "Click to open"} — start {fmtMonth(effStart)}{Number(t.percentDone) > 0 ? ` · ${t.percentDone}% done` : ""}
              </title>
            </rect>

            {/* Progress overlay — thin bright stripe on the bottom edge of the work bar */}
            {(() => {
              const pct = Math.max(0, Math.min(100, Number(t.percentDone) || 0));
              if (pct <= 0) return null;
              const barW = Math.max(0, clamp(endWork) - clamp(start));
              if (barW < 4) return null;
              return (
                <rect
                  x={clamp(start) + 2}
                  y={rowY + ROW_H - 12}
                  width={(barW - 4) * pct / 100}
                  height="3" rx="1.5"
                  fill="#fff" opacity="0.85"
                  pointerEvents="none"
                />
              );
            })()}

            {/* Recovery period */}
            {beVisible && endWork < endBE && (
              <a href={`#/task/${t.id}`}>
                <rect x={clamp(endWork)} y={rowY+12} width={Math.max(0, clamp(endBE)-clamp(endWork))}
                      height={ROW_H-24} fill="url(#hatch-amber)" opacity="0.8"/>
              </a>
            )}

            {/* Net-positive period */}
            {beVisible && endBE < endHorizon && (
              <a href={`#/task/${t.id}`}>
                <rect x={clamp(endBE)} y={rowY+12} width={Math.max(0, endHorizon-clamp(endBE))}
                      height={ROW_H-24} fill="var(--positive-bg)" stroke="var(--positive)" strokeWidth="1"/>
              </a>
            )}

            {/* If break-even falls past horizon, show recovery shading all the way out */}
            {endVisible && !beVisible && endWork < endHorizon && (
              <a href={`#/task/${t.id}`}>
                <rect x={clamp(endWork)} y={rowY+12} width={Math.max(0, endHorizon-clamp(endWork))}
                      height={ROW_H-24} fill="url(#hatch-amber)" opacity="0.6"/>
              </a>
            )}

            {/* Break-even marker */}
            {beVisible && (
              <g>
                <line x1={clamp(endBE)} y1={rowY+4} x2={clamp(endBE)} y2={rowY+ROW_H-4}
                      stroke="var(--positive)" strokeWidth="2" strokeDasharray="3,3"/>
                <text x={clamp(endBE)+4} y={rowY+ROW_H-6} fontSize="10" fill="var(--positive)">
                  BE
                </text>
              </g>
            )}

            {/* Horizontal-drag hint label */}
            {isHDragging && (
              <g pointerEvents="none">
                <rect x={clamp(start)} y={rowY-2} width={120} height={16} rx="3"
                      fill="#0b1220" stroke={colorWork} strokeWidth="1"/>
                <text x={clamp(start)+6} y={rowY+10} fontSize="10" fill="#e5e7eb">
                  Start: {fmtMonth(effStart)} (m {effStart.toFixed(1)})
                </text>
              </g>
            )}
          </g>
        );
      })}

      {/* Drop-target outline while reordering */}
      {vdrag && vdrag.moved && (
        <rect
          x="2" y={HEADER_H + vdrag.dropIndex*ROW_H + 1}
          width={chartWidth - 4} height={ROW_H - 2}
          fill="none" stroke="var(--positive)" strokeWidth="1.5" strokeDasharray="4,3"
          rx="3" pointerEvents="none"
        />
      )}

      {/* Dependency arrows (same-dept only) — follow live vIndex for reorder */}
      <g fill="none" stroke="var(--depline)" strokeWidth="1.3" strokeDasharray="4,3" markerEnd="url(#arr)">
        {rows.map(r => {
          const t = r.task;
          const deps = t.ganttDeps || [];
          return deps.map((depId, k) => {
            const sourceEcon = econById[depId];
            if (!sourceEcon) return null;
            const sVIndex = vIndexById[depId];
            const tVIndex = vIndexById[t.id];
            if (sVIndex == null || tVIndex == null) return null;
            const sx = LABEL_W + sourceEcon.completionMonth*MONTH_W;
            const tx = LABEL_W + (effStartById[t.id] ?? Number(t.startMonth))*MONTH_W;
            const sy = HEADER_H + sVIndex*ROW_H + ROW_H/2;
            const ty = HEADER_H + tVIndex*ROW_H + ROW_H/2;
            const midX = Math.max(sx + 14, tx - 10);
            return <path key={t.id+"-"+k} d={`M${sx},${sy} C${midX},${sy} ${midX},${ty} ${tx-2},${ty}`}/>;
          });
        })}
      </g>

      {/* External-gate dependency arrows — from milestone diamond to dependent task start */}
      <g fill="none" stroke="var(--milestone)" strokeWidth="1.2" strokeDasharray="2,3" opacity="0.85">
        {rows.map(r => {
          const t = r.task;
          const deps = (t.gateDeps || []);
          if (!deps.length) return null;
          const tVIndex = vIndexById[t.id];
          if (tVIndex == null) return null;
          const tx = LABEL_W + (effStartById[t.id] ?? Number(t.startMonth))*MONTH_W;
          const ty = HEADER_H + tVIndex*ROW_H + 8;   // top of the work bar
          return deps.map((mid, k) => {
            const ms = milestones.find(x => x.id === mid);
            if (!ms) return null;
            const idx = milestones.indexOf(ms);
            const mx = LABEL_W + effGateMonth(ms)*MONTH_W;
            const my = 56 + (idx%3)*10 + 5;          // bottom of diamond
            const midY = (my + ty) / 2;
            return (
              <path key={t.id+"-g-"+k}
                    d={`M${mx},${my} C${mx},${midY} ${tx},${midY} ${tx},${ty}`}/>
            );
          });
        })}
      </g>
    </svg>
  );
}
